import { createDeleteOperation } from '~/services/api/createDeleteOperation'
import { strictify } from '~/utils/strictify'
import { createRequestOperation } from '~/utils/createRequestOperation'
import {
  MenuAssignStoreMenusPathSchema,
  MenuAssignStoreMenusSuccessSchema,
  MenuBasicPathSchema,
  MenuBasicSuccessSchema,
  MenuCountsSuccessSchema,
  MenuDestroyPathSchema,
  MenuDestroySuccessSchema,
  MenuDuplicatePathSchema,
  MenuDuplicateSuccessSchema,
  MenuFormOptionsSuccessSchema,
  MenuMenuDetailsPathSchema,
  MenuMenuDetailsSuccessSchema,
  MenuOverviewPathSchema,
  MenuOverviewSuccessSchema,
  MenuStoreSuccessSchema,
  MenuUpdateOverviewPathSchema,
  MenuUpdateOverviewSuccessSchema,
  MenuUpdatePathSchema,
  MenuUpdateStatusPathSchema,
  MenuUpdateStatusSuccessSchema,
  MenuUpdateSuccessSchema,
  type MenuAssignStoreMenusRequest,
  type MenuOverviewQuery,
  type MenuStoreRequest,
  type MenuUpdateOverviewRequest,
  type MenuUpdateRequest,
  type MenuUpdateStatusRequest,
  type MenuDuplicateRequest
} from '~/services/api/generated'
import { MenuAssignStoreMenus } from '~/services/api/contracts/requests/MenuAssignStoreMenus'
import { MenuStore } from '~/services/api/contracts/requests/MenuStore'
import { MenuUpdate } from '~/services/api/contracts/requests/MenuUpdate'
import { MenuDuplicate } from '~/services/api/contracts/requests/MenuDuplicate'
import { MenuUpdateOverview } from '~/services/api/contracts/requests/MenuUpdateOverview'
import { MenuUpdateStatus } from '~/services/api/contracts/requests/MenuUpdateStatus'
import type { MenuOverview } from './interfaces'
import { MenuOverview as MenuOverviewRequest } from '~/services/api/contracts/requests/MenuOverview'

export function useMenusService() {
  const apiBaseURL = useRuntimeConfig().public.apiBaseUrl ?? ''
  const baseURL = `${apiBaseURL}/api/dashboard/menus`
  const sanctumClient = useApiRequest()
  const deleteMenu = createDeleteOperation(
    sanctumClient,
    (menuId: number) => MenuDestroyPathSchema.strict().parse({ menuId }),
    MenuDestroySuccessSchema,
    ({ menuId }) => `${baseURL}/${menuId}`
  )

  const updateMenu = createRequestOperation(
    sanctumClient,
    strictify(MenuUpdateSuccessSchema),
    (menuId: number, payload: MenuUpdateRequest) => ({
      options: { body: MenuUpdate({}).parse(payload), method: 'PUT' },
      url: `${baseURL}/${MenuUpdatePathSchema.strict().parse({ menuId }).menuId}`
    }),
    (response) => structuredClone(response.data)
  )

  const getMenuBasicById = createRequestOperation(
    sanctumClient,
    strictify(MenuBasicSuccessSchema),
    (menuId: number) => ({
      options: { globalErrorUi: true },
      url: `${baseURL}/${MenuBasicPathSchema.strict().parse({ menuId }).menuId}/basic`
    }),
    (response) => structuredClone(response.data)
  )

  const duplicateMenu = createRequestOperation(
    sanctumClient,
    strictify(MenuDuplicateSuccessSchema),
    (menuId: number, payload: MenuDuplicateRequest) => ({
      options: { body: MenuDuplicate({}).parse(payload), method: 'POST' },
      url: `${baseURL}/${MenuDuplicatePathSchema.strict().parse({ menuId }).menuId}/duplicate`
    }),
    (response) => structuredClone(response.data)
  )

  const updateMenuStatus = createRequestOperation(
    sanctumClient,
    strictify(MenuUpdateStatusSuccessSchema),
    (menuId: number, payload: MenuUpdateStatusRequest) => ({
      options: { body: MenuUpdateStatus({}).parse(payload), method: 'PUT' },
      url: `${baseURL}/${MenuUpdateStatusPathSchema.strict().parse({ menuId }).menuId}/status`
    }),
    (response) => structuredClone(response)
  )

  async function createMenu(payload: MenuStoreRequest) {
    const body = MenuStore({}).parse(payload)
    const response = strictify(MenuStoreSuccessSchema).parse(
      await sanctumClient(baseURL, { body, method: 'POST' })
    )
    return structuredClone(response.data)
  }

  async function getMenuFormOptions() {
    const response = MenuFormOptionsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/form-options`)
    )
    return structuredClone(response.data)
  }

  async function getMenuById(menuId: number) {
    const response = MenuMenuDetailsSuccessSchema.parse(
      await sanctumClient(
        `${baseURL}/${MenuMenuDetailsPathSchema.strict().parse({ menuId }).menuId}`,
        {
          globalErrorUi: true
        }
      )
    )
    return structuredClone(response.data)
  }

  async function getMenuCatalogCounts() {
    const response = MenuCountsSuccessSchema.parse(await sanctumClient(`${baseURL}/counts`))
    return structuredClone(response.data)
  }

  async function getMenuOverviewById(
    menuId: number,
    query: MenuOverviewQuery = {}
  ): Promise<MenuOverview> {
    const path = MenuOverviewPathSchema.strict().parse({ menuId })
    const parsedQuery = MenuOverviewRequest({}).parse(query)
    const response = MenuOverviewSuccessSchema.parse(
      await sanctumClient(`${baseURL}/${path.menuId}/overview`, {
        globalErrorUi: true,
        query: parsedQuery
      })
    )
    const { categories, ...overview } = structuredClone(response.data)
    return {
      ...overview,
      ...(categories == null ? {} : { categories })
    }
  }

  async function updateMenuOverview(
    menuId: number,
    payload: MenuUpdateOverviewRequest
  ): Promise<MenuOverview> {
    const path = MenuUpdateOverviewPathSchema.strict().parse({ menuId })
    const body = MenuUpdateOverview({}).parse(payload)
    const response = strictify(MenuUpdateOverviewSuccessSchema).parse(
      await sanctumClient(`${baseURL}/${path.menuId}/overview`, {
        body,
        method: 'PUT'
      })
    )
    const { categories, ...overview } = structuredClone(response.data)
    return {
      ...overview,
      ...(categories == null ? {} : { categories })
    }
  }

  async function assignStoreMenus(identifier: string, payload: MenuAssignStoreMenusRequest) {
    const path = MenuAssignStoreMenusPathSchema.strict().parse({ identifier })
    const body = MenuAssignStoreMenus({}).parse(payload)
    const response = strictify(MenuAssignStoreMenusSuccessSchema).parse(
      await sanctumClient(
        `${apiBaseURL}/api/dashboard/stores/${encodeURIComponent(path.identifier)}/menus`,
        { body, method: 'PUT' }
      )
    )
    return structuredClone(response.data)
  }

  return {
    assignStoreMenus,
    createMenu,
    deleteMenu,
    duplicateMenu,
    getMenuBasicById,
    getMenuById,
    getMenuCatalogCounts,
    getMenuFormOptions,
    getMenuOverviewById,
    updateMenu,
    updateMenuOverview,
    updateMenuStatus
  }
}
